import React, { useState, useEffect, useRef } from "react";
import { BASE_URL } from "utils/constant";
import CalendarPicker from "./CalenderPicker";
import { IoIosAttach } from "react-icons/io";
import {
  Image,
  Paperclip,
  MoreHorizontal,
  X,
  FileText,
  MoreVertical,
} from "lucide-react";
import { all } from "axios";
import { format, set, sub } from "date-fns";
import { useAlert } from "components/UI_components/Alert";
import { GoCheckCircleFill, GoCircle } from "react-icons/go";
import { MdDelete, MdEdit } from "react-icons/md";
import { FaSyncAlt } from "react-icons/fa";
import { UserAPI } from "api/dashboard/service";
import { taskApi } from "api/tasks/taskApi";
import { formatDateLong } from "utils/utilityFunctions";
import EditNameModal from "./EditNameModal";
import EditSubtaskModal from "./EditSubtaskModal";
import ImageAnnotatorModal from "components/ImageAnnotatorModal";
import ConfirmationModal from "components/ConfirmationModal";

/* ---------- small helpers ---------- */
const toArray = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw?.data?.results) return raw.data.results;
  if (raw?.data) return raw.data;
  return [];
};

const idsEqual = (left, right) => {
  if (left === null || left === undefined) return false;
  if (right === null || right === undefined) return false;
  return String(left) === String(right);
};

const normalizeAssignedIds = (subtaskData = {}) => {
  const source = Array.isArray(subtaskData?.assign_to)
    ? subtaskData.assign_to
    : Array.isArray(subtaskData?.assignee)
      ? subtaskData.assignee
      : [];

  return source
    .map((item) => {
      if (item && typeof item === "object") {
        return item.member_id ?? item.id ?? null;
      }
      return item;
    })
    .filter((id) => id !== null && id !== undefined && id !== "");
};

const normalizeAssigneeObjects = (subtaskData = {}) => {
  const source = Array.isArray(subtaskData?.assignee)
    ? subtaskData.assignee
    : Array.isArray(subtaskData?.assign_to)
      ? subtaskData.assign_to
      : [];

  return source.filter((item) => item && typeof item === "object");
};

const mapAssignedTaskMembers = (members = [], assignedIds = []) =>
  members.filter((member) =>
    assignedIds.some(
      (id) => idsEqual(id, member?.id) || idsEqual(id, member?.member_id),
    ),
  );

function extractFilenameFromUrl(url) {
  try {
    const parsedUrl = new URL(url);
    const pathSegments = parsedUrl.pathname.split("/");
    return pathSegments[pathSegments.length - 1];
  } catch (error) {
    console.error("Invalid URL:", error);
    return null;
  }
}

function convertDate(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null; // Check for invalid date

  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = months[date.getMonth()];

  return `${hours}:${minutes}, ${day} ${month}`;
}
// Helper function to extract file extension from URL
const getFileExtension = (url) => {
  if (!url) return "";
  // Remove query parameters
  const cleanUrl = url.split("?")[0];
  const parts = cleanUrl.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
};

// Thread helpers: link approval-response comments to their approval request update
const getApprovalRefIdFromComment = (comment = "") => {
  const m = String(comment).match(/#APPROVAL_(\d+)/i);
  return m ? Number(m[1]) : null;
};

const parseApprovalResponseComment = (comment = "") => {
  const raw = String(comment || "");
  const statusMatch = raw.match(/^Request\s+(approved|rejected)\.\s*/i);
  const statusWord = statusMatch?.[1]?.toLowerCase() || "";
  const messagePart = raw.split(/:\s*/).slice(1).join(": ").trim();
  return { statusWord, messagePart, raw };
};

const buildThreadedUpdates = (items = []) => {
  const childrenByParentId = new Map();
  const childIds = new Set();

  for (const u of items) {
    if (u?.is_request_for_approval) continue;
    const refId = getApprovalRefIdFromComment(u?.comment);
    if (!refId) continue;

    const child = { ...u, threadLevel: 1, threadParentId: refId };
    if (!childrenByParentId.has(refId)) childrenByParentId.set(refId, []);
    childrenByParentId.get(refId).push(child);
    childIds.add(u.id);
  }

  const threaded = [];
  for (const u of items) {
    if (childIds.has(u.id)) continue; // will be inserted under its parent
    threaded.push({ ...u, threadLevel: 0, threadParentId: null });

    const children = childrenByParentId.get(u.id);
    if (children?.length) {
      children.sort(
        (a, b) =>
          new Date(a.created_on).getTime() - new Date(b.created_on).getTime(),
      );
      threaded.push(...children);
    }
  }

  // If a child references a parent that isn't in the list, keep it visible as a normal update
  for (const u of items) {
    if (!childIds.has(u.id)) continue;
    const child = childrenByParentId
      .get(getApprovalRefIdFromComment(u?.comment))
      ?.find((x) => x.id === u.id);
    if (!child) continue;
    const parentExists = threaded.some((x) => x.id === child.threadParentId);
    if (!parentExists)
      threaded.push({ ...u, threadLevel: 0, threadParentId: null });
  }

  return threaded;
};
// Helper function to get file icon based on extension
const getFileIcon = (extension) => {
  const iconClass = "w-16 h-16";

  // Document files
  if (["doc", "docx"].includes(extension)) {
    return (
      <svg
        className={iconClass}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    );
  }

  // Spreadsheet files
  if (["xls", "xlsx"].includes(extension)) {
    return (
      <svg
        className={iconClass}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
        />
      </svg>
    );
  }

  // Archive files
  if (["zip", "rar", "7z", "tar", "gz"].includes(extension)) {
    return (
      <svg
        className={iconClass}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
        />
      </svg>
    );
  }

  // Video files
  if (["mp4", "avi", "mov", "wmv", "flv", "mkv"].includes(extension)) {
    return (
      <svg
        className={iconClass}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
        />
      </svg>
    );
  }

  // Audio files
  if (["mp3", "wav", "ogg", "flac", "m4a"].includes(extension)) {
    return (
      <svg
        className={iconClass}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
        />
      </svg>
    );
  }

  // Default file icon
  return (
    <svg
      className={iconClass}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  );
};

const initialsOf = (f = "", l = "") =>
  (f[0] || "").toUpperCase() + (l[0] || "").toUpperCase();

const getInitials = (name = "") => {
  const [first, last] = name.split(" ");
  return initialsOf(first, last);
};

/* ------------------------------------------------------------------------ */
/*  SubtaskDetailComponent                                                  */
/* ------------------------------------------------------------------------ */
const SubtaskDetailComponent = ({
  subtask,
  taskId,
  onClose,
  refetchSubtasks,
  isVisible = true,
}) => {
  const { addAlert } = useAlert();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const organizationEmail = String(
    JSON.parse(localStorage.getItem("organization") || "{}")?.user_email || "",
  ).toLowerCase();

  /* ---------------- authentication / ids ---------------- */
  const token = localStorage.getItem("access");
  const authHeaders = { Authorization: `Bearer ${token}` };
  const parentTaskId = taskId ?? subtask.task_id;
  const [trigger, setTrigger] = useState(0);
  const [currentTab, setCurrentTab] = useState("Updates");
  const [allApprovals, setAllApprovals] = useState([]);
  const [currentStatus, setCurrentStatus] = useState("pending");
  const [currentApprovals, setCurrentApprovals] = useState([]);
  const [subtaskName, setSubtaskName] = useState(
    subtask.subtask_name || subtask.name || "",
  );

  /* ------------------- local state ---------------------- */
  const [updates, setUpdates] = useState([]);
  const [approvalsFetched, setApprovalsFetched] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const [loadingUpd, setLoadingUpd] = useState(false);
  const [isFileViewing, setIsFileViewing] = useState(false);
  const [fileUrl, setFileUrl] = useState("");
  const [startDate, setStartDate] = useState(subtask.start_date || null);
  const [endDate, setEndDate] = useState(subtask.end_date || null);
  const [completedBy, setCompletedBy] = useState(subtask.completed_by || null);
  const [completedOn, setCompletedOn] = useState(subtask.completed_on || null);
  const [isCompleted, setIsCompleted] = useState(subtask.is_completed || false);
  const [isUpdatingDates, setIsUpdatingDates] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [file, setFile] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const fileInputRef = useRef();
  const imageInputRef = useRef();
  const [attachmentType, setAttachmentType] = useState(null);
  const [subtaskDetails, setSubtaskDetails] = useState(
    subtask.subtask_details || "",
  );
  const [isUpdatingDetails, setIsUpdatingDetails] = useState(false);
  const [isEditDescription, setIsEditDescription] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [userData, setUserData] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingSubtask, setIsDeletingSubtask] = useState(false);
  const [myUserpic, setMyUserpic] = useState(null);
  const [subtaskActionsOpen, setSubtaskActionsOpen] = useState(false);
  const [hasTaskAuthority, setHasTaskAuthority] = useState(false);
  const isSubtaskLocked = isCompleted;
  const canEditSubtaskMeta = hasTaskAuthority && !isSubtaskLocked;

  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || extractFilenameFromUrl(url) || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback to opening in new tab if fetch fails (e.g., CORS)
      window.open(url, "_blank");
    }
  };

  const fetchSubtask = async () => {
    try {
      const res = await fetch(`${BASE_URL}/task/get-all-subtasks/`, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ task_id: subtask.task_id }),
      });
      const data = await res.json();
      if (data.data) {
        const subtaskData = data.data.find((s) => s.id === subtask.id);
        if (subtaskData) {
          setStartDate(subtaskData.start_date || null);
          setEndDate(subtaskData.end_date || null);
          setSubtaskName(subtaskData.subtask_name || subtaskData.name || "");
          setSubtaskDetails(subtaskData.subtask_details || "");
          setCompletedBy(subtaskData.completed_by || null);
          setCompletedOn(subtaskData.completed_on || null);
          setIsCompleted(subtaskData.is_completed || false);
          const assignedIds = normalizeAssignedIds(subtaskData);
          const assignedMembers = normalizeAssigneeObjects(subtaskData);
          setPickedIds(assignedIds);
          setAssignees(
            assignedMembers.length > 0
              ? assignedMembers
              : mapAssignedTaskMembers(taskMembers, assignedIds),
          );
        }
      }
    } catch (error) {
      console.error("Error fetching subtask:", error);
    }
  };

  useEffect(() => {
    setSubtaskName(subtask.subtask_name || subtask.name || "");
  }, [subtask.subtask_name, subtask.name, subtask.id]);

  useEffect(() => {
    setSubtaskDetails(subtask.subtask_details || "");
  }, [subtask.subtask_details, subtask.id]);

  useEffect(() => {
    setIsCompleted(subtask.is_completed || false);
  }, [subtask.is_completed, subtask.id]);

  useEffect(() => {
    setCompletedBy(subtask.completed_by || null);
  }, [subtask.completed_by, subtask.id]);

  useEffect(() => {
    setCompletedOn(subtask.completed_on || null);
  }, [subtask.completed_on, subtask.id]);

  useEffect(() => {
    setStartDate(subtask.start_date || null);
    setEndDate(subtask.end_date || null);
  }, [subtask.start_date, subtask.end_date, subtask.id]);

  useEffect(() => {
    setAssignees(normalizeAssigneeObjects(subtask));
    setPickedIds(normalizeAssignedIds(subtask));
  }, [subtask]);

  // members & approvers
  const [taskMembers, setTaskMembers] = useState([]);
  const [assignees, setAssignees] = useState(() =>
    normalizeAssigneeObjects(subtask),
  );
  const [approvers, setApprovers] = useState([]);

  // dropdown helpers
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pickedIds, setPickedIds] = useState(() =>
    normalizeAssignedIds(subtask),
  );
  const dropdownRef = useRef(null);
  const subtaskActionsRef = useRef(null);
  const [assigning, setAssigning] = useState(false);

  const [approverDropdownOpen, setApproverDropdownOpen] = useState(false);
  const [pickedApproverIds, setPickedApproverIds] = useState([]);
  const approverDropdownRef = useRef(null);
  const [assigningApprovers, setAssigningApprovers] = useState(false);

  //Upload thing should be in a state
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  // Image annotation modal state
  const [showAnnotator, setShowAnnotator] = useState(false);
  const [annotatingFile, setAnnotatingFile] = useState(null);

  // approval dialog
  const [approvalDialog, setApprovalDialog] = useState({
    isOpen: false,
    updateId: null,
    requesting: false,
  });

  const renderTextforApprover = (update) => {
    if (update.is_request_for_approval) {
      if (update.status === "approved") {
        return "Approved";
      }
      if (update.status === "rejected") {
        return "Rejected";
      }
      return isApprover ? "Approve Update" : "Request Pending";
    } else {
      return isApprover ||
        update.created_by.split(" | ")[0] !== userData?.user_email
        ? "View File"
        : "Request Approval";
    }
  };

  const renderButtonClass = (update) => {
    if (update.is_request_for_approval) {
      if (update.status === "approved") {
        return "bg-[#168C1A1A] text-[#168C1A]";
      }
      if (update.status === "rejected") {
        return "bg-[#C213131A] text-[#C21313]";
      }
      return "bg-sm-primary text-white hover:bg-sm-primary/80";
    } else {
      return "bg-sm-primary text-white hover:bg-sm-primary/80";
    }
  };

  const handleFileClick = (update) => {
    return async () => {
      if (isSubtaskLocked) {
        if (update?.file_url) {
          setFileUrl(update.file_url);
          setIsFileViewing(true);
        }
        return;
      }

      const showFile = () => {
        setFileUrl(update.file_url);
        setIsFileViewing(true);
      };

      if (update.is_request_for_approval) {
        if (["approved", "rejected"].includes(update.status) || !isApprover) {
          showFile();
        } else {
          setSelectedUpdate(update);
          setShowApproveModal(true);
        }
      } else {
        if (
          isApprover ||
          update.created_by.split(" | ")[0] !== userData?.user_email
        ) {
          showFile();
        } else {
          setApprovalDialog({
            isOpen: true,
            updateId: update.id,
            requesting: false,
          });
        }
      }
    };
  };

  const fetchCurrentApprovals = async () => {
    try {
      const r = await fetch(
        `${BASE_URL}/task/subtask/approval-requests/${subtask.id}/?status=${currentStatus}`,
        {
          headers: authHeaders,
        },
      );
      const payload = toArray(await r.json());
      setCurrentApprovals(
        payload?.map((a) => ({
          id: a.update_id,
          update_id: a.update_id,
          is_request_for_approval: true,
          status: a.approval_status,
          file_url: a.file_url,
          created_by: a.created_by,
          created_on: a.created_on,
          comment: a.comment,
        })),
      );
    } catch (e) {
      console.error("Failed to fetch current approvals", e);
      setCurrentApprovals([]);
    }
  };
  useEffect(() => {
    fetchCurrentApprovals();
  }, [subtask.id, currentStatus]);

  /* ------------------- fetch all approvals -------------------- */
  const fetchAllApprovals = async () => {
    setApprovalsFetched(false);
    try {
      const r = await fetch(
        `${BASE_URL}/task/subtask/approval-requests/${subtask.id}/`,
        {
          headers: authHeaders,
        },
      );
      const payload = toArray(await r.json());
      setAllApprovals(payload);
    } catch (e) {
      console.error("Failed to fetch approvals", e);
      setAllApprovals([]);
    } finally {
      setApprovalsFetched(true);
    }
  };
  useEffect(() => {
    fetchAllApprovals();
  }, [subtask.id]);

  /* ------------------- fetch user data ------------------ */
  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const parsedUser = JSON.parse(user);
        setUserData(parsedUser);
      } catch (error) {
        console.error("Failed to parse user data:", error);
      }
    }
  }, []);
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        subtaskActionsRef.current &&
        !subtaskActionsRef.current.contains(event.target)
      ) {
        setSubtaskActionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);
  useEffect(() => {
    if (!isSubtaskLocked) return;
    setDropdownOpen(false);
    setApproverDropdownOpen(false);
    setSubtaskActionsOpen(false);
  }, [isSubtaskLocked]);

  useEffect(() => {
    const fetchSiteMembersForAuthority = async () => {
      try {
        const siteData = JSON.parse(localStorage.getItem("siteData") || "{}");
        if (!siteData?.id) return;
        const response = await taskApi.getSiteMembers(siteData.id);
        if (response?.status) {
          const currentMember = response.data.find(
            (member) => member.user_id === user.user,
          );
          setHasTaskAuthority(
            currentMember?.create_taskgroup ||
              currentMember?.is_admin ||
              currentMember?.joined_as === "Owner",
          );
        }
      } catch (e) {
        setHasTaskAuthority(false);
      }
    };

    fetchSiteMembersForAuthority();
  }, [user.user]);

  // Fetch current user's userpic via API
  useEffect(() => {
    (async () => {
      try {
        const res = await UserAPI.getUserDetails();
        if (res?.status) {
          setMyUserpic(res?.data?.userpic || null);
        }
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  /* ------------------- auto-scroll ---------------------- */
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [updates]);

  /* ------------------- fetch updates -------------------- */
  const fetchUpdates = async () => {
    if (!approvalsFetched) return;
    if (updates.length === 0) {
      setLoadingUpd(true);
    }
    try {
      const r = await fetch(
        `${BASE_URL}/task/subtasks/${subtask.id}/updates/`,
        { headers: authHeaders },
      );
      const payload = toArray(await r.json()).reverse();

      const flat = payload?.map((u) => {
        const emailPart = String(u.created_by || "").split("|")[0] || "";
        const normalizedEmail = emailPart.trim().toLowerCase();
        return {
          id: u.id,
          comment: u.comment,
          created_by: u.created_by,
          createdByEmail: normalizedEmail,
          userpic: u.userpic,
          is_request_for_approval: u.is_request_for_approval,
          created_on: u.created_on,
          file_url: u.file_url,
          status:
            allApprovals.find((a) => a.update_id === u.id)?.approval_status ||
            "",
        };
      });

      setUpdates(buildThreadedUpdates(flat));
    } catch {
      // setUpdates([]); // Don't clear updates on error to keep existing ones visible
    } finally {
      setLoadingUpd(false);
    }
  };

  useEffect(() => {
    if (approvalsFetched) {
      fetchUpdates();
    }
  }, [subtask.id, approvalsFetched]);

  /* ------------------- fetch task members (once) -------- */
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE_URL}/task/get-task-members/`, {
          method: "POST",
          headers: {
            ...authHeaders,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ task_id: parentTaskId }),
        });
        const members = toArray(await r.json());
        // console.log("Task members:", members);
        setTaskMembers(members);

        setApprovers(members.filter((m) => m.is_approver));
        setIsApprover(
          members.some((m) => m.user_id === user.user && m.is_approver),
        );
      } catch {
        setTaskMembers([]);
      }
    })();
  }, [parentTaskId, trigger]);

  /* ------------------- fetch current subtask assignees & approvers -------------------- */
  useEffect(() => {
    if (taskMembers.length === 0) return;
    const assignedIds = normalizeAssignedIds(subtask);
    if (assignedIds.length === 0) {
      setAssignees([]);
      return;
    }
    setAssignees(mapAssignedTaskMembers(taskMembers, assignedIds));
  }, [taskMembers, subtask]);

  /* ------------------- date change handler --------------- */
  const handleDateChange = async (newStartDate, newEndDate) => {
    if (!hasTaskAuthority || isSubtaskLocked) return;
    if (!newStartDate || !newEndDate) return;
    setIsUpdatingDates(true);
    try {
      const res = await fetch(
        `${BASE_URL}/task/update-subtask/${subtask.id}/`,
        {
          method: "PUT",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({
            subtask_name: subtask.subtask_name || subtask.name,
            start_date: newStartDate
              ? format(newStartDate, "yyyy-MM-dd")
              : null,
            end_date: newEndDate ? format(newEndDate, "yyyy-MM-dd") : null,
            task_id: subtask.task_id,
          }),
        },
      );

      const data = await res.json();
      if (data.detail) {
        addAlert(
          "You do not have permission to update subtask dates. You must have Task Authority permission",
          "error",
        );
        setStartDate(subtask.start_date || null);
        setEndDate(subtask.end_date || null);
        return;
      }

      setStartDate(newStartDate);
      setEndDate(newEndDate);
      refetchSubtasks(subtask.task_group, subtask.task_id);
    } catch (e) {
      console.error("Update dates failed", e);
      addAlert("Could not update dates.", "error");
    } finally {
      setIsUpdatingDates(false);
    }
  };

  /* ------------------- handle subtask update -------- */
  const handleSubtaskUpdate = async (newName, newDescription) => {
    if (!hasTaskAuthority || isSubtaskLocked) return;
    try {
      const res = await fetch(
        `${BASE_URL}/task/update-subtask/${subtask.id}/`,
        {
          method: "PUT",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({
            subtask_name: newName,
            subtask_details: newDescription,
            task_id: subtask.task_id,
          }),
        },
      );

      const data = await res.json();
      if (data.detail) {
        addAlert(
          "You do not have permission to update subtask. You must have Task Authority permission",
          "error",
        );
        return;
      }

      setSubtaskName(newName);
      setSubtaskDetails(newDescription);
      refetchSubtasks(subtask.task_group, subtask.task_id);
      addAlert("Subtask updated", "success");
    } catch (e) {
      console.error("Update subtask failed", e);
      addAlert("Could not update subtask.", "error");
    }
  };

  const handleRenameSubtask = async (nextName) => {
    await handleSubtaskUpdate(nextName, subtaskDetails);
  };

  /*---------------------subtask delete handler        */
  const handleDeleteSubtask = () => {
    if (isSubtaskLocked) return;
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteSubtask = async () => {
    setIsDeletingSubtask(true);
    try {
      const res = await fetch(
        `${BASE_URL}/task/delete-subtask/${subtask.id}/`,
        {
          method: "DELETE",
          headers: authHeaders,
        },
      );

      if (res.ok) {
        addAlert("Subtask deleted successfully", "success");
        refetchSubtasks(subtask.task_group, subtask.task_id);
        onClose(); // Close the panel after deletion
      } else {
        const data = await res.json();
        if (data.detail) {
          addAlert(
            "You do not have permission to delete this subtask. You must have Task Authority permission",
            "error",
          );
        } else {
          addAlert("Failed to delete subtask", "error");
        }
      }
    } catch (e) {
      console.error("Delete subtask failed", e);
      addAlert("Could not delete subtask.", "error");
    } finally {
      setIsDeletingSubtask(false);
      setIsDeleteModalOpen(false);
    }
  };

  /* ------------------- handle subtask is_completed status ---------------- */
  const handleCompletionToggle = async () => {
    if (!hasTaskAuthority) return;
    // if (!isApprover) {
    //   addAlert("Only approvers can mark subtasks as complete.", "error");
    //   return;
    // }

    if (!isCompleted) {
      const pendingApprovals = allApprovals.filter(
        (a) => a.approval_status === null,
      );
      if (pendingApprovals.length > 0) {
        addAlert(
          "Please approve or reject all pending approvals before marking the subtask as complete.",
          "warning",
        );
        setCurrentTab("Approvals");
        setCurrentStatus("pending");
        return;
      }
    }

    try {
      const res = await fetch(
        `${BASE_URL}/task/update-subtask/${subtask.id}/`,
        {
          method: "PUT",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({
            subtask_name: subtask.subtask_name || subtask.name,
            is_completed: !isCompleted,
            task_id: subtask.task_id,
          }),
        },
      );
      const data = await res.json();
      if (data.detail) {
        addAlert(
          "You do not have permission to update subtask status. You must have Task Authority permission",
          "error",
        );
        return;
      }

      addAlert(
        isCompleted ? "Subtask marked as incomplete" : "Subtask completed",
        "success",
      );
      setIsCompleted((prev) => !prev);
      setCompletedOn(data.data.completed_on || null);
      setCompletedBy(data.data.completed_by || null);
      refetchSubtasks(subtask.task_group, subtask.task_id);
    } catch (e) {
      console.error("Toggle completion failed", e);
      addAlert("Could not update subtask status.", "error");
    }
  };

  /* ------------------- handle subtask details change -------- */
  const handleDetailsChange = async () => {
    if (isSubtaskLocked) return;
    setIsUpdatingDetails(true);
    try {
      const res = await fetch(
        `${BASE_URL}/task/update-subtask/${subtask.id}/`,
        {
          method: "PUT",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({
            subtask_name: subtask.subtask_name || subtask.name,
            subtask_details: subtaskDetails,
            is_completed: subtask.is_completed ?? false,
            start_date: subtask.start_date,
            end_date: subtask.end_date,
            task_id: subtask.task_id,
          }),
        },
      );

      const data = await res.json();
      if (data.detail) {
        addAlert(
          "You do not have permission to update subtask details. You must have Task Authority permission",
          "error",
        );
        setSubtaskDetails(subtask.subtask_details || "");
        return;
      }

      refetchSubtasks(subtask.task_group, subtask.task_id);
      addAlert("Subtask details updated", "success");
    } catch (e) {
      console.error("Update details failed", e);
      addAlert("Could not update description.", "error");
    } finally {
      setIsUpdatingDetails(false);
    }
  };

  /* ------------------- assign helpers ------------------ */
  const toggleDropdown = () => {
    if (!hasTaskAuthority || isSubtaskLocked) return;
    setDropdownOpen((o) => !o);
  };

  const togglePick = (memberId) =>
    setPickedIds((p = []) =>
      p.some((id) => idsEqual(id, memberId))
        ? p.filter((id) => !idsEqual(id, memberId))
        : [...p, memberId],
    );

  const handleAssignMembers = async () => {
    if (!hasTaskAuthority || isSubtaskLocked) return;
    setAssigning(true);
    try {
      const res = await fetch(
        `${BASE_URL}/task/update-subtask/${subtask.id}/`,
        {
          method: "PUT",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({
            subtask_name: subtask.subtask_name || subtask.name,
            task_id: subtask.task_id,
            assign_to: Array.isArray(pickedIds) ? pickedIds : [],
          }),
        },
      );

      const data = await res.json();
      if (data.detail) {
        addAlert(
          "You do not have permission to assign subtask members.You must have Task Authority permission",
          "error",
        );
        setDropdownOpen(false);
        return;
      }

      const newlyAssignedMembers = mapAssignedTaskMembers(
        taskMembers,
        Array.isArray(pickedIds) ? pickedIds : [],
      );

      // setAssignees((prev) => {
      //   const existingIds = prev.map((member) => member.id);
      //   const uniqueNewMembers = newlyAssignedMembers.filter(
      //     (member) => !existingIds.includes(member.id)
      //   );
      //   return [...prev, ...uniqueNewMembers];
      // });
      setAssignees(newlyAssignedMembers);
      refetchSubtasks(subtask.task_group, subtask.task_id);

      // setPickedIds([]);
      setDropdownOpen(false);
    } catch (e) {
      console.error("Assign members failed", e);
      addAlert("Could not assign members.", "error");
    } finally {
      setAssigning(false);
    }
  };

  /* ------------------- approver assign helpers ------------------ */
  const toggleApproverDropdown = () => {
    if (isSubtaskLocked) return;
    setApproverDropdownOpen((o) => !o);
    setPickedApproverIds(approvers?.map((a) => a.id));
  };

  const toggleApproverPick = (memberId) =>
    setPickedApproverIds((p) =>
      p.includes(memberId)
        ? p.filter((id) => id !== memberId)
        : [...p, memberId],
    );

  const handleAssignApprovers = async () => {
    if (isSubtaskLocked) return;
    if (!pickedApproverIds.length) return;
    setAssigningApprovers(true);
    try {
      const res = await fetch(
        `${BASE_URL}/task/tasks/${parentTaskId}/assign-approvers/`,
        {
          method: "POST",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ approver_ids: pickedApproverIds }),
        },
      );
      if (!res.ok) throw new Error("Failed to assign approvers");

      setTaskMembers((prev) =>
        prev?.map((m) =>
          pickedApproverIds.includes(m.id) ? { ...m, is_approver: true } : m,
        ),
      );
      setApprovers(taskMembers.filter((m) => pickedApproverIds.includes(m.id)));

      // setPickedApproverIds([]);
      setApproverDropdownOpen(false);
      await onRefresh();
      addAlert("Approvers assigned successfully  successfully!", "success");
    } catch (e) {
      console.error("Assign approvers failed", e);
      addAlert("You do not have permission to assign approvers.", "error");
    } finally {
      setAssigningApprovers(false);
    }
  };

  /* ------------------- outside click close for dropdowns -------- */
  useEffect(() => {
    if (!dropdownOpen) return;
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [dropdownOpen]);

  useEffect(() => {
    if (!approverDropdownOpen) return;
    const onClick = (e) => {
      if (
        approverDropdownRef.current &&
        !approverDropdownRef.current.contains(e.target)
      ) {
        setApproverDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [approverDropdownOpen]);

  /* ------------------- comment handlers ----------------- */
  const resetCommentBox = () => {
    setNewComment("");
    setEditingId(null);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const handleSubmitComment = async () => {
    if (isSubtaskLocked) return;
    const trimmed = newComment.trim();
    if (!trimmed && !file) return;

    // Prevent multiple submissions
    if (isSubmittingComment) return;
    setIsSubmittingComment(true);

    try {
      let body, headers;
      if (file) {
        const fd = new FormData();
        fd.append("comment", trimmed);
        fd.append("file", file);
        body = fd;
        headers = { Authorization: `Bearer ${token}` };
      } else {
        body = JSON.stringify({ comment: trimmed });
        headers = {
          ...authHeaders,
          "Content-Type": "application/json",
        };
      }
      if (editingId === null) {
        await fetch(`${BASE_URL}/task/subtasks/${subtask.id}/updates/create/`, {
          method: "POST",
          headers,
          body,
        });
      } else {
        await fetch(`${BASE_URL}/task/subtasks/updates/${editingId}/`, {
          method: "PATCH",
          headers: {
            ...authHeaders,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ comment: trimmed }),
        });
      }
      await fetchUpdates();
      resetCommentBox();
    } catch (e) {
      console.error("Comment save failed", e);
      addAlert("Could not add or update comment.", "error");
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleDelete = async (id) => {
    const update = updates.find((u) => u.id === id);
    const canDelete =
      organizationEmail && update?.createdByEmail === organizationEmail;
    if (!canDelete) {
      addAlert("You can only delete updates you created.", "error");
      return;
    }

    try {
      await fetch(`${BASE_URL}/task/subtasks/updates/${id}/delete/`, {
        method: "DELETE",
        headers: authHeaders,
      });
      setUpdates((u) => u.filter((x) => x.id !== id));
    } catch (e) {
      console.error("Delete failed", e);
      addAlert("Could not delete comment.", "error");
    }
  };

  /* ------------------- helpers ------------------------- */
  const renderAvatar = (member) =>
    member?.userpic ? (
      <img
        key={member.id}
        src={member.userpic}
        alt={`${member.first_name} ${member.last_name}`}
        className="w-8 h-8 -ml-2 rounded-full object-cover border-2 border-white"
        title={`${member.first_name} ${member.last_name}`}
      />
    ) : (
      <div
        key={member.id}
        className="w-8 h-8 p-1 rounded-full -ml-2 bg-[#343C6A] text-white text-[14px] flex items-center justify-center border-2 border-white"
        title={`${member.first_name} ${member.last_name}`}
      >
        {initialsOf(member.first_name, member.last_name)}
      </div>
    );

  const renderApproverAvatar = (member) =>
    member?.userpic ? (
      <img
        key={member.id}
        src={member.userpic}
        alt={`${member.first_name} ${member.last_name}`}
        className="w-8 h-8 rounded-full object-cover border-2 border-white"
        title={`${member.first_name} ${member.last_name}`}
      />
    ) : (
      <div
        key={member.id}
        className="w-8 h-8 rounded-full bg-[#343C6A] text-white text-[14px] flex items-center justify-center border-2 border-white"
        title={`${member.first_name} ${member.last_name}`}
      >
        {initialsOf(member.first_name, member.last_name)}
      </div>
    );

  /* ------------------- approve update -------------------------- */
  const approveSubtaskUpdate = async (updateId, status, comment) => {
    try {
      console.log(
        "Approving subtask update with ID:",
        updateId,
        "Status:",
        status,
      );
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("access")}`,
        "Content-Type": "application/json",
      };
      const updated_comment = `Regarding #APPROVAL_${updateId}: ${comment}`;

      const response = await fetch(
        `${BASE_URL}/task/subtasks/updates/${updateId}/approve-reject/`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ action: status, comment: updated_comment }),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      // await fetchAllApprovals();
      // await fetchUpdates();
      // await fetchCurrentApprovals();
      return await response.json();
    } catch (error) {
      console.error("Error approving subtask update:", error);
      throw error;
    }
  };

  const onRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchSubtask();
      await fetchAllApprovals();
      await fetchUpdates();
      setTrigger((t) => t + 1);
      await fetchCurrentApprovals();
      // addAlert("Data refreshed successfully", "success");
      // addAlert("Data refreshed successfully", "success");
    } catch (error) {
      console.error("Error refreshing data:", error);
      addAlert("Failed to refresh data", "error");
    } finally {
      setIsRefreshing(false);
    }
  };

  /* ==================================================================== */
  /*                                UI                                    */
  /* ==================================================================== */
  return (
    <div
      className={`fixed inset-y-0 right-0 w-[45vw] bg-white shadow-lg z-50 flex flex-col border-l-[1px] border-[#78797A] transition-transform duration-300 ease-in-out ${
        isVisible ? "translate-x-0" : "translate-x-full"
      }`}
    >
      {/* ---------- header ---------- */}
      <div className="flex flex-col w-full bg-white border-b border-gray-100 p-3">
        {/* Top Row: Title & Actions */}
        <div className="flex justify-between items-start gap-4">
          {/* Left: Title Area */}
          <div className="flex items-start flex-1 min-w-0">
            <div className="flex items-center">
              <h2
                onClick={() => {
                  if (canEditSubtaskMeta) setShowRenameModal(true);
                }}
                className={`text-xl font-medium text-gray-900 transition-colors line-clamp-2 leading-tight tracking-tight ${
                  canEditSubtaskMeta
                    ? "cursor-pointer hover:text-purple-700"
                    : "cursor-default"
                } ${isSubtaskLocked ? "cursor-not-allowed opacity-70" : ""}`}
                title={canEditSubtaskMeta ? "Click to edit subtask name" : ""}
              >
                {subtaskName}
              </h2>
            </div>

            <EditNameModal
              isOpen={canEditSubtaskMeta && showRenameModal}
              title="Edit Subtask Name"
              placeholder="Enter subtask name"
              initialValue={subtaskName}
              onClose={() => setShowRenameModal(false)}
              onSave={handleRenameSubtask}
            />
          </div>

          {/* Right: Actions Toolbar */}
          <div className="flex items-center space-x-2 pl-2">
            {/* Subtask Actions Menu */}
            <div className="relative" ref={subtaskActionsRef}>
              {hasTaskAuthority && (
                <button
                  onClick={() => setSubtaskActionsOpen((prev) => !prev)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  title="Subtask actions"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              )}
              {/* {subtaskActionsOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-50 overflow-hidden">
                  {hasTaskAuthority && (
                    <button
                      onClick={() => {
                        if (!canEditSubtaskMeta) return;
                        setSubtaskActionsOpen(false);
                        setIsEditingName(true);
                      }}
                      disabled={!canEditSubtaskMeta}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${!canEditSubtaskMeta
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                      <MdEdit className="w-4 h-4" />
                      Edit subtask
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (isSubtaskLocked) return;
                      setSubtaskActionsOpen(false);
                      handleDeleteSubtask();
                    }}
                    disabled={isSubtaskLocked}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${isSubtaskLocked
                      ? "text-red-300 cursor-not-allowed"
                      : "text-red-600 hover:bg-red-50"
                      }`}
                  >
                    <MdDelete className="w-4 h-4" />
                    Delete subtask
                  </button>
                </div>
              )} */}
              {subtaskActionsOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-50 overflow-hidden">
                  {hasTaskAuthority && (
                    <button
                      onClick={() => {
                        if (!canEditSubtaskMeta) return;
                        setSubtaskActionsOpen(false);
                        setShowDetailsModal(true);
                      }}
                      disabled={!canEditSubtaskMeta}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                        !canEditSubtaskMeta
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <MdEdit className="w-4 h-4" />
                      Edit details
                    </button>
                  )}
                  {hasTaskAuthority && (
                    <button
                      onClick={() => {
                        if (isSubtaskLocked) return;
                        setSubtaskActionsOpen(false);
                        handleDeleteSubtask();
                      }}
                      disabled={isSubtaskLocked}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                        isSubtaskLocked
                          ? "text-red-300 cursor-not-allowed"
                          : "text-red-600 hover:bg-red-50"
                      }`}
                    >
                      <MdDelete className="w-4 h-4" />
                      Delete subtask
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Refresh */}
            <button
              onClick={onRefresh}
              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              title="Refresh"
            >
              <FaSyncAlt
                className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin text-purple-600" : ""}`}
              />
            </button>

            {/* Status Toggle */}
            {hasTaskAuthority && (
              <button
                onClick={handleCompletionToggle}
                className={`
          group flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border
          ${
            isCompleted
              ? "bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
              : "bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
          }
        `}
              >
                {isCompleted ? (
                  <GoCheckCircleFill className="w-4 h-4" />
                ) : (
                  <GoCircle className="w-4 h-4 text-gray-400 group-hover:text-purple-500" />
                )}
                <span>{isCompleted ? "Completed" : "Mark Complete"}</span>
              </button>
            )}

            {/* Close Panel */}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Bottom Row: Meta Info */}
        {!showRenameModal && !showDetailsModal && subtask?.created_by && (
          <div className="flex flex-row items-center justify-between">
            <div className="flex items-center text-xs text-gray-400 space-x-3 mt-1">
              <div className="flex items-center">
                <span className="font-medium text-gray-500 mr-1">
                  Created by:
                </span>
                {subtask.created_by}
              </div>

              {isCompleted && (
                <>
                  <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                  <div className="flex items-center">
                    <span className="font-medium text-gray-500 mr-1">
                      Completed by:
                    </span>
                    {completedBy || subtask?.created_by}
                    <span className="mx-1">on</span>
                    {formatDateLong(completedOn) ||
                      formatDateLong(subtask?.end_date)}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ---------- info grid ---------- */}
      <div className="grid grid-cols-3 gap-4 p-4 pt-2 border-b-[1px] border-black">
        {/* Assignee box */}
        <div className="flex flex-col relative border-[1px] border-[#3B0069] rounded-lg px-2 py-2">
          <label className="text-sm text-black mb-2">Subtask Assignee</label>
          <div className="flex px-1 items-center">
            {assignees?.length
              ? assignees.slice(0, 3)?.map((member) => renderAvatar(member))
              : null}
            {assignees?.length > 3 && (
              <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-700 text-[10px] flex items-center justify-center border-2 border-white">
                +{assignees?.length - 3}
              </div>
            )}
            {hasTaskAuthority && (
              <button
                onClick={toggleDropdown}
                disabled={isSubtaskLocked}
                className={`w-7 h-7 rounded-full bg-white border border-gray-300 text-gray-600 flex flex-col items-center text-[13px] justify-center transition-colors ${
                  isSubtaskLocked
                    ? "cursor-not-allowed opacity-60"
                    : "hover:bg-gray-100"
                }`}
                title="Assign members"
              >
                +
              </button>
            )}
          </div>

          {/* dropdown */}
          {hasTaskAuthority && dropdownOpen && (
            <div
              ref={dropdownRef}
              className="absolute top-full left-0 mt-2 w-64 bg-white shadow-lg border rounded z-40"
            >
              <ul className="max-h-60 overflow-y-auto">
                {taskMembers?.length > 0 ? (
                  taskMembers?.map((m) => {
                    const memberId = m.id;
                    const isAlreadyAssigned = assignees.some(
                      (assignee) => assignee.member_id === memberId,
                    );

                    return (
                      <li
                        key={memberId}
                        className={`flex items-center px-4 py-2 hover:bg-gray-50 `}
                      >
                        <input
                          type="checkbox"
                          checked={pickedIds.includes(memberId)}
                          onChange={() => togglePick(memberId)}
                          disabled={isSubtaskLocked}
                          className="mr-2"
                        />
                        <div className="flex items-center space-x-2">
                          {m?.userpic ? (
                            <img
                              src={m.userpic}
                              alt={`${m.first_name} ${m.last_name}`}
                              className="w-7 h-7 rounded-full object-cover "
                            />
                          ) : (
                            <div className="w-7 h-7 p-2 bg-gray-100 rounded-full flex items-center justify-center">
                              <span className="text-[10px] font-medium text-gray-600">
                                {m.first_name?.[0]}
                                {m.last_name?.[0]}
                              </span>
                            </div>
                          )}
                          <span className="text-sm">
                            {m.first_name} {m.last_name}
                            {isAlreadyAssigned && " (Already assigned)"}
                          </span>
                        </div>
                      </li>
                    );
                  })
                ) : (
                  <li className="px-4 py-2 text-sm text-gray-500">
                    No members found
                  </li>
                )}
              </ul>
              <div className="flex justify-end border-t p-2">
                <button
                  onClick={handleAssignMembers}
                  disabled={assigning || isSubtaskLocked}
                  className="px-3 py-1 bg-sm-primary text-white rounded text-sm disabled:opacity-50"
                >
                  {assigning ? "Saving…" : "Assign"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Due date */}
        <div className="flex flex-col border-[1px] border-[#3B0069] rounded-lg px-2 py-2">
          <label className="text-sm text-black mb-2">Date Range</label>
          {hasTaskAuthority ? (
            <div
              className={`relative ${isSubtaskLocked ? "pointer-events-none opacity-60" : ""}`}
              aria-disabled={isSubtaskLocked}
            >
              <CalendarPicker
                startDate={startDate}
                endDate={endDate}
                onDateChange={handleDateChange}
                placeholder="Select dates"
              />
              {isUpdatingDates && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-2 text-sm text-gray-600">
              {startDate || endDate
                ? `${startDate ? formatDateLong(startDate) : "No start date"} - ${endDate ? formatDateLong(endDate) : "No end date"}`
                : "No date range set"}
            </div>
          )}
        </div>

        {/* Approver section */}
        <div className="flex flex-col relative border-[1px] border-[#3B0069] rounded-lg px-2 py-1">
          <label className="text-sm text-black mb-2">Approver</label>
          <div className="rounded p-2 h-10 flex items-center space-x-1">
            {approvers.length
              ? approvers
                  .slice(0, 3)
                  .map((member) => renderApproverAvatar(member))
              : null}
            {approvers.length > 3 && (
              <div className="w-6 h-6 rounded-full bg-gray-300 text-gray-700 text-[10px] flex items-center justify-center border-2 border-white">
                +{approvers.length - 3}
              </div>
            )}
            {/* <button
              onClick={toggleApproverDropdown}
              disabled={isSubtaskLocked}
              className={`w-6 h-6 rounded-full bg-white border border-gray-300 text-gray-600 flex items-center justify-center ml-1 transition-colors ${
                isSubtaskLocked
                  ? "cursor-not-allowed opacity-60"
                  : "hover:bg-gray-100"
              }`}
              title="Assign approvers"
            >
              +
            </button> */}
            {hasTaskAuthority && (
              <button
                onClick={toggleApproverDropdown}
                disabled={isSubtaskLocked}
                className={`w-6 h-6 rounded-full bg-white border border-gray-300 text-gray-600 flex items-center justify-center ml-1 transition-colors ${
                  isSubtaskLocked
                    ? "cursor-not-allowed opacity-60"
                    : "hover:bg-gray-100"
                }`}
                title="Assign approvers"
              >
                +
              </button>
            )}
          </div>

          {approverDropdownOpen && (
            <div
              ref={approverDropdownRef}
              className="absolute top-full right-0 mt-2 w-64 bg-white shadow-lg border rounded z-40"
            >
              <ul className="max-h-60 overflow-y-auto">
                {taskMembers?.length ? (
                  taskMembers?.map((m) => {
                    const isAlready = approvers?.some((a) => a.id === m.id);
                    return (
                      <li
                        key={m.id}
                        className={`flex items-center px-4 py-2 hover:bg-gray-50`}
                      >
                        <input
                          type="checkbox"
                          className="mr-2"
                          // disabled={isAlready}
                          checked={pickedApproverIds.includes(m.id)}
                          onChange={() => toggleApproverPick(m.id)}
                          disabled={isSubtaskLocked}
                        />
                        <div className="flex items-center space-x-2">
                          {m?.userpic ? (
                            <img
                              src={m.userpic}
                              alt={`${m.first_name} ${m.last_name}`}
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                              <span className="text-[10px] font-medium text-gray-600">
                                {m.first_name?.[0]}
                                {m.last_name?.[0]}
                              </span>
                            </div>
                          )}
                          <span className="text-sm">
                            {m.first_name} {m.last_name}
                            {isAlready && " (Already approver)"}
                          </span>
                        </div>
                      </li>
                    );
                  })
                ) : (
                  <li className="px-4 py-2 text-sm text-gray-500">
                    No members found
                  </li>
                )}
              </ul>
              <div className="flex justify-end border-t p-2">
                <button
                  onClick={handleAssignApprovers}
                  disabled={
                    !pickedApproverIds.length ||
                    assigningApprovers ||
                    isSubtaskLocked
                  }
                  className="px-3 py-1 bg-sm-primary text-white rounded text-sm disabled:opacity-50"
                >
                  {assigningApprovers ? "Saving…" : "Assign"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---------- updates list ---------- */}
      <div className="flex-1 flex flex-col  h-[60vh] pb-0">
        <div
          className="flex flex-row items-center w-full px-2 py-2"
          style={{ gap: 20 }}
        >
          <div
            className={`flex-row items-center border-b-[3px] cursor-pointer px-6 ${
              currentTab === "Updates" ? "border-[#8067E0]" : ""
            }`}
            onClick={() => setCurrentTab("Updates")}
          >
            Updates
          </div>
          <div className="flex-row items-center bg-[#78797A]  h-[25px] w-[2px]"></div>
          <div
            className={`flex-row items-center cursor-pointer px-6 border-b-[3px] ${
              currentTab === "Approvals" ? "border-[#8067E0]" : ""
            }`}
            onClick={() => setCurrentTab("Approvals")}
          >
            Approvals
          </div>
        </div>

        {currentTab === "Updates" ? (
          <>
            <div
              className="flex-1 px-6 py-4 h-[70%] bg-gray-50/30"
              style={{ overflowY: "scroll" }}
            >
              {loadingUpd ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-purple-800 rounded-full animate-pulse"></div>
                    <p className="text-sm text-gray-600 font-medium">
                      Loading updates...
                    </p>
                  </div>
                </div>
              ) : // ) : updates.length ? (
              updates.length || isCompleted ? (
                <div className="relative">
                  <div className="absolute left-5 top-2 bottom-2 w-px bg-gray-200" />
                  <div className="space-y-4">
                    {/* {isCompleted && (
                      <div className="rounded-xl p-1 relative z-10">
                        <div className="flex items-start space-x-3">
                          <div className="relative flex-shrink-0 z-10">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white flex items-center justify-center shadow-sm">
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                  Subtask Completed
                                </span>
                              </div>
                              <span className="text-xs text-gray-500 font-medium">
                                {convertDate(completedOn)}
                              </span>
                            </div>
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                              <p className="text-sm text-gray-800 leading-relaxed">
                                Subtask completed by{" "}
                                <span className="font-medium">
                                  {completedBy || subtask?.created_by}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )} */}
                    {updates?.map((u) => (
                      <div
                        key={u.id}
                        className={`rounded-xl p-1 relative z-10 ${
                          u.threadLevel === 1 ? "ml-10" : ""
                        }`}
                      >
                        {u.threadLevel === 1 && (
                          <div className="absolute -left-5 top-6 h-6 w-5 border-l-2 border-b-2 border-gray-200 rounded-bl-lg" />
                        )}
                        <div className="flex items-start space-x-3">
                          {/* Enhanced User Avatar */}
                          <div className="relative flex-shrink-0 z-10">
                            {u.userpic ? (
                              <img
                                src={u.userpic}
                                alt="User avatar"
                                className={`rounded-full object-cover shadow-sm bg-white ${
                                  u.threadLevel === 1 ? "w-7 h-7" : "w-8 h-8"
                                }`}
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#343C6A] to-[#4A5578] text-white flex items-center justify-center text-sm font-semibold shadow-sm">
                                {u.created_by?.includes("|")
                                  ? getInitials(u.created_by.split("|")[1])
                                  : getInitials(u.created_by)}
                              </div>
                            )}
                            {u.is_request_for_approval && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                                <svg
                                  className="w-2.5 h-2.5 text-white"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            )}
                          </div>

                          {/* Comment Content */}
                          <div className="flex-1 min-w-0">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-semibold text-gray-900">
                                  {u.created_by?.includes("|")
                                    ? u.created_by.split("|")[1]
                                    : u.created_by}
                                </span>
                                {u.threadLevel === 1 &&
                                  (() => {
                                    const parsed = parseApprovalResponseComment(
                                      u.comment,
                                    );
                                    if (!parsed.statusWord) return null;
                                    const isApproved =
                                      parsed.statusWord === "approved";
                                    return (
                                      <span
                                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                          isApproved
                                            ? "bg-green-100 text-green-800"
                                            : "bg-red-100 text-red-800"
                                        }`}
                                      >
                                        {isApproved ? "Approved" : "Rejected"}
                                      </span>
                                    );
                                  })()}
                                {u.is_request_for_approval && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                    <svg
                                      className="w-3 h-3 mr-1"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    APPROVAL #{u.id}
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-gray-500 font-medium">
                                {convertDate(u.created_on)}
                              </span>
                            </div>

                            {editingId === u.id ? (
                              <div className="space-y-3">
                                <div className="relative">
                                  <textarea
                                    className="w-full border border-gray-200 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                    rows={3}
                                    value={newComment}
                                    onChange={(e) =>
                                      setNewComment(e.target.value)
                                    }
                                    placeholder="Write your update..."
                                    disabled={isSubtaskLocked}
                                  />
                                </div>
                                <div className="flex justify-end space-x-2">
                                  <button
                                    onClick={resetCommentBox}
                                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={handleSubmitComment}
                                    className="px-4 py-1.5 bg-purple-800 text-white text-sm rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                                    disabled={
                                      !newComment.trim() || isSubtaskLocked
                                    }
                                  >
                                    Save Changes
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-3 select-text">
                                {u.threadLevel === 1 ? (
                                  (() => {
                                    const parsed = parseApprovalResponseComment(
                                      u.comment,
                                    );
                                    const body =
                                      parsed.messagePart || parsed.raw;
                                    return (
                                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                                          {body}
                                        </p>
                                      </div>
                                    );
                                  })()
                                ) : (
                                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                                    {u.comment}
                                  </p>
                                )}

                                {/* Enhanced File Attachment */}
                                {u.file_url && (
                                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 hover:bg-gray-100/50 transition-colors">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                                        <div
                                          className="relative flex-shrink-0 cursor-pointer"
                                          onClick={() => {
                                            setFileUrl(u.file_url);
                                            setIsFileViewing(true);
                                          }}
                                        >
                                          {(() => {
                                            const fileExtension =
                                              getFileExtension(u.file_url);

                                            // Image files - show thumbnail
                                            if (
                                              [
                                                "jpg",
                                                "jpeg",
                                                "png",
                                                "gif",
                                                "webp",
                                                "svg",
                                                "bmp",
                                              ].includes(fileExtension)
                                            ) {
                                              return (
                                                <>
                                                  <img
                                                    src={u.file_url}
                                                    alt="Update attachment"
                                                    className="w-12 h-12 object-cover rounded-md hover:opacity-80 transition-opacity"
                                                  />
                                                  <div className="absolute inset-0 bg-black/0 hover:bg-black/10 rounded-md transition-colors"></div>
                                                </>
                                              );
                                            }

                                            // PDF files - show PDF icon
                                            if (fileExtension === "pdf") {
                                              return (
                                                <div className="w-12 h-12 bg-red-100 rounded-md flex items-center justify-center hover:bg-red-200 transition-colors">
                                                  <svg
                                                    className="w-7 h-7 text-red-600"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth={2}
                                                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                                    />
                                                  </svg>
                                                </div>
                                              );
                                            }

                                            // Text files - show text icon
                                            if (
                                              [
                                                "txt",
                                                "md",
                                                "csv",
                                                "log",
                                                "json",
                                                "xml",
                                              ].includes(fileExtension)
                                            ) {
                                              return (
                                                <div className="w-12 h-12 bg-blue-100 rounded-md flex items-center justify-center hover:bg-blue-200 transition-colors">
                                                  <svg
                                                    className="w-7 h-7 text-blue-600"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth={2}
                                                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                    />
                                                  </svg>
                                                </div>
                                              );
                                            }

                                            // Document files
                                            if (
                                              ["doc", "docx"].includes(
                                                fileExtension,
                                              )
                                            ) {
                                              return (
                                                <div className="w-12 h-12 bg-blue-100 rounded-md flex items-center justify-center hover:bg-blue-200 transition-colors">
                                                  <svg
                                                    className="w-7 h-7 text-blue-600"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth={2}
                                                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                    />
                                                  </svg>
                                                </div>
                                              );
                                            }

                                            // Spreadsheet files
                                            if (
                                              ["xls", "xlsx"].includes(
                                                fileExtension,
                                              )
                                            ) {
                                              return (
                                                <div className="w-12 h-12 bg-green-100 rounded-md flex items-center justify-center hover:bg-green-200 transition-colors">
                                                  <svg
                                                    className="w-7 h-7 text-green-600"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth={2}
                                                      d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                    />
                                                  </svg>
                                                </div>
                                              );
                                            }

                                            // Archive files
                                            if (
                                              [
                                                "zip",
                                                "rar",
                                                "7z",
                                                "tar",
                                                "gz",
                                              ].includes(fileExtension)
                                            ) {
                                              return (
                                                <div className="w-12 h-12 bg-yellow-100 rounded-md flex items-center justify-center hover:bg-yellow-200 transition-colors">
                                                  <svg
                                                    className="w-7 h-7 text-yellow-600"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth={2}
                                                      d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                                                    />
                                                  </svg>
                                                </div>
                                              );
                                            }

                                            // Video files
                                            if (
                                              [
                                                "mp4",
                                                "avi",
                                                "mov",
                                                "wmv",
                                                "flv",
                                                "mkv",
                                              ].includes(fileExtension)
                                            ) {
                                              return (
                                                <div className="w-12 h-12 bg-purple-100 rounded-md flex items-center justify-center hover:bg-purple-200 transition-colors">
                                                  <svg
                                                    className="w-7 h-7 text-purple-600"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth={2}
                                                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                    />
                                                  </svg>
                                                </div>
                                              );
                                            }

                                            // Audio files
                                            if (
                                              [
                                                "mp3",
                                                "wav",
                                                "ogg",
                                                "flac",
                                                "m4a",
                                              ].includes(fileExtension)
                                            ) {
                                              return (
                                                <div className="w-12 h-12 bg-pink-100 rounded-md flex items-center justify-center hover:bg-pink-200 transition-colors">
                                                  <svg
                                                    className="w-7 h-7 text-pink-600"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth={2}
                                                      d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                                                    />
                                                  </svg>
                                                </div>
                                              );
                                            }

                                            // Default file icon
                                            return (
                                              <div className="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center hover:bg-gray-300 transition-colors">
                                                <svg
                                                  className="w-7 h-7 text-gray-600"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                                  />
                                                </svg>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                        <div className="flex flex-col min-w-0 flex-1">
                                          <span className="text-sm font-medium text-gray-900 truncate">
                                            {extractFilenameFromUrl(
                                              u.file_url,
                                            ) || "Attachment"}
                                          </span>
                                          <span className="text-xs text-gray-500">
                                            Uploaded {convertDate(u.created_on)}
                                          </span>
                                        </div>
                                      </div>
                                      <button
                                        onClick={handleFileClick(u)}
                                        disabled={isSubtaskLocked}
                                        className={`rounded-lg ${renderButtonClass(u)}
                                                  px-3 py-1 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
                                        title="Request approval for this update"
                                      >
                                        {renderTextforApprover(u)}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Action Icons */}
                          {editingId !== u.id && (
                            <div className="flex items-start space-x-1 flex-shrink-0">
                              {/* {organizationEmail &&
                              u.createdByEmail === organizationEmail && (
                                <button
                                  onClick={() => handleDelete(u.id)}
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete update"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                </button>
                              )} */}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {isCompleted && (
                      <div className="rounded-xl p-1 relative z-10">
                        <div className="flex items-start space-x-3">
                          <div className="relative flex-shrink-0 z-10">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white flex items-center justify-center shadow-sm">
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                  Subtask Completed
                                </span>
                              </div>
                              <span className="text-xs text-gray-500 font-medium">
                                {convertDate(completedOn)}
                              </span>
                            </div>
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                              <p className="text-sm text-gray-800 leading-relaxed">
                                Subtask completed by{" "}
                                <span className="font-medium">
                                  {completedBy || subtask?.created_by}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-0">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500 font-medium">
                    No updates yet
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Updates and comments will appear here
                  </p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* ---------- add comment ---------- */}
            {!isCompleted && (
              <div className="border-t px-4 py-2 bg-white">
                {file && (
                  <div className="mb-3 p-3 bg-[#F4F2EE] rounded-lg flex items-center justify-between border border-gray-200">
                    <div className="flex items-center space-x-3 overflow-hidden">
                      <div className="p-2 bg-white rounded border border-gray-200 flex items-center justify-center">
                        {file.type.startsWith("image/") ? (
                          <Image size={20} className="text-gray-600" />
                        ) : (
                          <FileText size={20} className="text-gray-600" />
                        )}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {(file.size / 1024).toFixed(1)} KB • Attached
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {file.type &&
                        file.type.startsWith &&
                        file.type.startsWith("image/") && (
                          <button
                            onClick={() => {
                              setAnnotatingFile(file);
                              setShowAnnotator(true);
                            }}
                            className="px-3 py-1 rounded-md bg-white border text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            title="Annotate image"
                          >
                            Annotate
                          </button>
                        )}
                      <button
                        onClick={() => setFile(null)}
                        className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                        title="Remove attachment"
                      >
                        <X size={18} className="text-gray-500" />
                      </button>
                    </div>
                  </div>
                )}

                <div className="relative group">
                  <textarea
                    rows={3}
                    className="w-full border-none rounded-t-lg p-3 text-sm bg-[#F4F2EE] focus:ring-0 resize-none min-h-[100px] transition-all placeholder-gray-500"
                    placeholder="Write a message..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                  />
                  <div className="flex items-center justify-between px-3 py-2 bg-[#F4F2EE] rounded-b-lg border-t border-gray-200">
                    <div className="flex items-center space-x-1">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={(e) => setFile(e.target.files[0])}
                        className="hidden"
                      />
                      <input
                        type="file"
                        ref={imageInputRef}
                        onChange={(e) => setFile(e.target.files[0])}
                        className="hidden"
                        accept="image/*"
                      />
                      <button
                        onClick={() => {
                          imageInputRef.current?.click();
                        }}
                        className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                        title="Add image"
                      >
                        <Image size={20} />
                      </button>
                      <button
                        onClick={() => {
                          fileInputRef.current?.click();
                        }}
                        className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                        title="Attach file"
                      >
                        <Paperclip size={20} />
                      </button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={handleSubmitComment}
                        disabled={
                          (!newComment.trim() && !file) || isSubmittingComment
                        }
                        className={`px-4 py-1 rounded-full text-sm font-semibold transition-colors ${
                          (!newComment.trim() && !file) || isSubmittingComment
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "bg-[#0a66c2] text-white hover:bg-[#004182]"
                        }`}
                      >
                        {isSubmittingComment ? "Sending..." : "Send"}
                      </button>
                      {/* <button className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors">
                      <MoreHorizontal size={20} />
                    </button> */}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col px-2 py-2">
            <div
              className="flex flex-row items-center justify-start mb-2 px-4 py-2"
              style={{ gap: 10 }}
            >
              <div
                className={`flex-row items-center flex cursor-pointer rounded-2xl px-3 py-1  font-medium text-sm ${
                  currentStatus === "pending"
                    ? "bg-[#ECE6F0] border-[1px] text-[#3B0069] border-btn-bg "
                    : "bg-[#E3E3E3] text-[#737373] "
                }`}
                onClick={() => setCurrentStatus("pending")}
              >
                Pending
              </div>
              <div
                className={`flex-row items-center flex cursor-pointer rounded-2xl px-3 py-1  font-medium text-sm ${
                  currentStatus === "approved"
                    ? "bg-[#ECE6F0] border-[1px] text-[#3B0069] border-btn-bg "
                    : "bg-[#E3E3E3] text-[#737373] "
                }`}
                onClick={() => setCurrentStatus("approved")}
              >
                Approved
              </div>
              <div
                className={`flex-row items-center flex cursor-pointer rounded-2xl px-3 py-1  font-medium text-sm ${
                  currentStatus === "rejected"
                    ? "bg-[#ECE6F0] border-[1px] text-[#3B0069] border-btn-bg "
                    : "bg-[#E3E3E3] text-[#737373] "
                }`}
                onClick={() => setCurrentStatus("rejected")}
              >
                Rejected
              </div>
            </div>
            {currentStatus === "pending" && (
              <div className="text-sm text-gray-500 mb-2 px-4">
                {currentApprovals.length} approvals requires your approval
              </div>
            )}
            {currentStatus === "approved" && (
              <div className="text-sm text-gray-500 mb-2 px-4">
                {currentApprovals.length} updates approved
              </div>
            )}
            {currentStatus === "rejected" && (
              <div className="text-sm text-gray-500 mb-2 px-4">
                {currentApprovals.length} updates rejected
              </div>
            )}
            <div
              className="flex flex-col px-4 h-[35vh]"
              style={{
                overflowY: "scroll",
              }}
            >
              {currentApprovals?.map((u) => (
                <div className="flex w-full justify-between flex-row px-3 py-2 items-center bg-white rounded-lg border-[1px] border-[#78797A] mt-1">
                  <div
                    className="flex flex-row w-[70%] items-center "
                    style={{
                      gap: 10,
                    }}
                  >
                    <div
                      className="relative flex-shrink-0 cursor-pointer"
                      onClick={() => {
                        setFileUrl(u.file_url);
                        setIsFileViewing(true);
                      }}
                    >
                      {(() => {
                        const fileExtension = getFileExtension(u.file_url);

                        // Image files - show thumbnail
                        if (
                          [
                            "jpg",
                            "jpeg",
                            "png",
                            "gif",
                            "webp",
                            "svg",
                            "bmp",
                          ].includes(fileExtension)
                        ) {
                          return (
                            <>
                              <img
                                src={u.file_url}
                                alt="Update attachment"
                                className="w-12 h-12 object-cover rounded-md hover:opacity-80 transition-opacity"
                              />
                              <div className="absolute inset-0 bg-black/0 hover:bg-black/10 rounded-md transition-colors"></div>
                            </>
                          );
                        }

                        // PDF files - show PDF icon
                        if (fileExtension === "pdf") {
                          return (
                            <div className="w-12 h-12 bg-red-100 rounded-md flex items-center justify-center hover:bg-red-200 transition-colors">
                              <svg
                                className="w-7 h-7 text-red-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                />
                              </svg>
                            </div>
                          );
                        }

                        // Text files - show text icon
                        if (
                          ["txt", "md", "csv", "log", "json", "xml"].includes(
                            fileExtension,
                          )
                        ) {
                          return (
                            <div className="w-12 h-12 bg-blue-100 rounded-md flex items-center justify-center hover:bg-blue-200 transition-colors">
                              <svg
                                className="w-7 h-7 text-blue-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            </div>
                          );
                        }

                        // Document files
                        if (["doc", "docx"].includes(fileExtension)) {
                          return (
                            <div className="w-12 h-12 bg-blue-100 rounded-md flex items-center justify-center hover:bg-blue-200 transition-colors">
                              <svg
                                className="w-7 h-7 text-blue-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            </div>
                          );
                        }

                        // Spreadsheet files
                        if (["xls", "xlsx"].includes(fileExtension)) {
                          return (
                            <div className="w-12 h-12 bg-green-100 rounded-md flex items-center justify-center hover:bg-green-200 transition-colors">
                              <svg
                                className="w-7 h-7 text-green-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                            </div>
                          );
                        }

                        // Archive files
                        if (
                          ["zip", "rar", "7z", "tar", "gz"].includes(
                            fileExtension,
                          )
                        ) {
                          return (
                            <div className="w-12 h-12 bg-yellow-100 rounded-md flex items-center justify-center hover:bg-yellow-200 transition-colors">
                              <svg
                                className="w-7 h-7 text-yellow-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                                />
                              </svg>
                            </div>
                          );
                        }

                        // Video files
                        if (
                          ["mp4", "avi", "mov", "wmv", "flv", "mkv"].includes(
                            fileExtension,
                          )
                        ) {
                          return (
                            <div className="w-12 h-12 bg-purple-100 rounded-md flex items-center justify-center hover:bg-purple-200 transition-colors">
                              <svg
                                className="w-7 h-7 text-purple-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                            </div>
                          );
                        }

                        // Audio files
                        if (
                          ["mp3", "wav", "ogg", "flac", "m4a"].includes(
                            fileExtension,
                          )
                        ) {
                          return (
                            <div className="w-12 h-12 bg-pink-100 rounded-md flex items-center justify-center hover:bg-pink-200 transition-colors">
                              <svg
                                className="w-7 h-7 text-pink-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                                />
                              </svg>
                            </div>
                          );
                        }

                        // Default file icon
                        return (
                          <div className="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center hover:bg-gray-300 transition-colors">
                            <svg
                              className="w-7 h-7 text-gray-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="text-sm text-black font-medium">
                        {extractFilenameFromUrl(u.file_url) ||
                          "No file attached"}
                      </span>
                      <span className="text-xs text-gray-600">
                        {convertDate(u.created_on) || "Unknown date"}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleFileClick(u)}
                    className={`rounded-lg ${
                      currentStatus === "approved"
                        ? "bg-[#168C1A1A] text-[#168C1A]"
                        : currentStatus === "rejected"
                          ? "bg-[#C213131A] text-[#C21313]"
                          : "bg-btn-bg text-white hover:bg-purple-800"
                    }  px-3 py-1 `}
                    title="Request approval for this update"
                  >
                    {renderTextforApprover(u)}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ---------- approval confirmation dialog ---------- */}
      {approvalDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Request Approval</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to request approval for this update?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() =>
                  setApprovalDialog({
                    isOpen: false,
                    updateId: null,
                    requesting: false,
                  })
                }
                disabled={approvalDialog.requesting || isSubtaskLocked}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                No
              </button>
              <button
                onClick={async () => {
                  if (!approvalDialog.updateId) return;
                  setApprovalDialog((prev) => ({ ...prev, requesting: true }));
                  try {
                    const response = await fetch(
                      `${BASE_URL}/task/subtasks/updates/${approvalDialog.updateId}/request-approval/`,
                      {
                        method: "POST",
                        headers: {
                          ...authHeaders,
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          comment: "Requesting approval",
                        }),
                      },
                    );
                    if (response.ok) {
                      addAlert(
                        "Approval request sent successfully!",
                        "success",
                      );
                      setApprovalDialog({
                        isOpen: false,
                        updateId: null,
                        requesting: false,
                      });
                      await fetchUpdates();
                    } else {
                      throw new Error("Failed to request approval");
                    }
                  } catch (e) {
                    console.error("Request approval failed", e);
                    addAlert(
                      "Could not request approval. Please try again.",
                      "error",
                    );
                  } finally {
                    setApprovalDialog((prev) => ({
                      ...prev,
                      requesting: false,
                    }));
                  }
                }}
                disabled={approvalDialog.requesting || isSubtaskLocked}
                className="px-4 py-2 bg-btn-bg text-white rounded hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {approvalDialog.requesting ? "Requesting..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isFileViewing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  File Preview
                </h3>
                <span className="text-sm text-gray-500">
                  {extractFilenameFromUrl(fileUrl)}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() =>
                    handleDownload(fileUrl, extractFilenameFromUrl(fileUrl))
                  }
                  className="inline-flex items-center px-3 py-2 bg-purple-800 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download
                </button>
                <button
                  onClick={() => setIsFileViewing(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              {(() => {
                const fileExtension = getFileExtension(fileUrl);
                const fileName = extractFilenameFromUrl(fileUrl);

                // Image files
                if (
                  ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(
                    fileExtension,
                  )
                ) {
                  return (
                    <div className="flex items-center justify-center h-full">
                      <img
                        src={fileUrl}
                        alt={fileName}
                        className="max-h-full max-w-full object-contain rounded-lg"
                      />
                    </div>
                  );
                }

                // PDF files
                if (fileExtension === "pdf") {
                  return (
                    <iframe
                      src={fileUrl}
                      className="w-full h-full min-h-[70vh] rounded-lg border border-gray-200"
                      title="PDF Preview"
                    />
                  );
                }

                // Text files
                if (
                  ["txt", "md", "csv", "log", "json", "xml"].includes(
                    fileExtension,
                  )
                ) {
                  return (
                    <div className="h-full">
                      <iframe
                        src={fileUrl}
                        className="w-full h-full min-h-[70vh] rounded-lg border border-gray-200 bg-gray-50"
                        title="Text Preview"
                      />
                    </div>
                  );
                }

                // Other file types - show icon and download
                return (
                  <div className="flex flex-col items-center justify-center h-full space-y-4">
                    <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                      {getFileIcon(fileExtension)}
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-medium text-gray-900 mb-1">
                        {fileName}
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        {fileExtension.toUpperCase()} file - Preview not
                        available
                      </p>
                      <button
                        onClick={() => handleDownload(fileUrl, fileName)}
                        className="inline-flex items-center px-4 py-2 bg-purple-800 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                        Download File
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Image annotator modal for draft attachments */}
      {showAnnotator && annotatingFile && (
        <ImageAnnotatorModal
          imageFile={annotatingFile}
          onClose={() => {
            setShowAnnotator(false);
            setAnnotatingFile(null);
          }}
          onSave={async (blob, options = {}) => {
            try {
              // Create a File from blob. Preserve original name if replacing.
              const originalName =
                (annotatingFile && annotatingFile.name) || "annotated.png";
              const fileName = options.replaceOriginal
                ? originalName
                : `annotated-${originalName}`;
              const newFile = new File([blob], fileName, {
                type: blob.type || "image/png",
              });

              // Replace the draft file with annotated file so upload will use it
              setFile(newFile);

              // clear file input so user can reselect if needed (optional but useful)
              try {
                if (fileInputRef.current) fileInputRef.current.value = "";
                if (imageInputRef.current) imageInputRef.current.value = "";
              } catch (e) {
                // ignore
              }
            } catch (e) {
              console.error("Failed to apply annotated image:", e);
            } finally {
              setShowAnnotator(false);
              setAnnotatingFile(null);
            }
          }}
        />
      )}

      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  Approve Update
                </h3>
                <span className="text-sm text-gray-500">
                  {extractFilenameFromUrl(selectedUpdate.file_url)}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() =>
                    handleDownload(
                      selectedUpdate.file_url,
                      extractFilenameFromUrl(selectedUpdate.file_url),
                    )
                  }
                  className="inline-flex items-center px-3 py-2 bg-purple-800 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download
                </button>
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* File Preview Content */}
            <div className="flex-1 overflow-auto p-6">
              {(() => {
                const fileExtension = getFileExtension(selectedUpdate.file_url);
                const fileName = extractFilenameFromUrl(
                  selectedUpdate.file_url,
                );

                // Image files
                if (
                  ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(
                    fileExtension,
                  )
                ) {
                  return (
                    <div className="flex items-center justify-center h-full min-h-[50vh]">
                      <img
                        src={selectedUpdate.file_url}
                        alt={fileName}
                        className="max-h-full max-w-full object-contain rounded-lg"
                      />
                    </div>
                  );
                }

                // PDF files
                if (fileExtension === "pdf") {
                  return (
                    <iframe
                      src={selectedUpdate.file_url}
                      className="w-full h-full min-h-[50vh] rounded-lg border border-gray-200"
                      title="PDF Preview"
                    />
                  );
                }

                // Text files
                if (
                  ["txt", "md", "csv", "log", "json", "xml"].includes(
                    fileExtension,
                  )
                ) {
                  return (
                    <div className="h-full min-h-[50vh]">
                      <iframe
                        src={selectedUpdate.file_url}
                        className="w-full h-full rounded-lg border border-gray-200 bg-gray-50"
                        title="Text Preview"
                      />
                    </div>
                  );
                }

                // Other file types - show icon and download
                return (
                  <div className="flex flex-col items-center justify-center h-full min-h-[50vh] space-y-4">
                    <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                      {getFileIcon(fileExtension)}
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-medium text-gray-900 mb-1">
                        {fileName}
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        {fileExtension.toUpperCase()} file - Preview not
                        available
                      </p>
                      <button
                        onClick={() =>
                          handleDownload(selectedUpdate.file_url, fileName)
                        }
                        className="inline-flex items-center px-4 py-2 bg-purple-800 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                        Download File
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Comment Section */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <textarea
                className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                rows={3}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment (optional)"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center p-4 border-t border-gray-200 bg-white">
              <button
                onClick={() => setShowApproveModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={async () => {
                    try {
                      await approveSubtaskUpdate(
                        selectedUpdate.id,
                        "reject",
                        newComment,
                      );
                      addAlert("Update rejected successfully!", "success");
                      setShowApproveModal(false);
                      setNewComment("");
                      onRefresh();
                    } catch (e) {
                      console.error("Rejection failed", e);
                      addAlert(
                        "Could not reject update. Please try again.",
                        "error",
                      );
                    }
                  }}
                  disabled={isSubtaskLocked}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Issue detected
                </button>
                <button
                  onClick={async () => {
                    try {
                      await approveSubtaskUpdate(
                        selectedUpdate.id,
                        "approve",
                        newComment,
                      );
                      setShowApproveModal(false);
                      addAlert("Update approved successfully!", "success");
                      setNewComment("");
                      onRefresh();
                    } catch (e) {
                      console.error("Approval failed", e);
                      addAlert(
                        "Could not approve update. Please try again.",
                        "error",
                      );
                    }
                  }}
                  disabled={isSubtaskLocked}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Approve file
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <EditSubtaskModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        onSave={handleSubtaskUpdate}
        initialName={subtaskName}
        initialDescription={subtaskDetails}
        startInEditMode={true}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDeleteSubtask}
        title="Delete Subtask"
        message="Are you sure you want to delete this subtask? This action cannot be undone."
        confirmText="Delete"
        isDeleting={isDeletingSubtask}
      />
    </div>
  );
};

export default SubtaskDetailComponent;
